import { AttendanceSidepanelCompensationBlockFragment } from 'generated/resources/graphql'
import { DeepPartial } from 'utility-types'

import { formatBackendDateRange } from 'modules/core/lib/dates'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

class CompensationBlockUtils {
  static name(block?: {
    compensationType?: AttendanceSidepanelCompensationBlockFragment['compensationType']
    categoryName?: AttendanceSidepanelCompensationBlockFragment['categoryName']
  }): string {
    if (block?.categoryName) {
      return block.categoryName
    }

    if (CompensationBlockUtils.isExtraHoursFromBankOfHours(block)) {
      return 'extra_hours_from_bank_of_hours'
    }

    return ''
  }

  /**
   * Adjusts category name based on whether complementary hours labels should be used
   * Replaces "Extra hours" with "Complementary hours" when complementary hours is enabled for part-time employees
   */
  static adjustCategoryNameForEmployeeType(categoryName: string, useComplementaryHoursLabels: boolean): string {
    if (categoryName === 'Extra hours' && useComplementaryHoursLabels) {
      return i18n.t('clock_in.sidepanel.compensation_overview.complementary_hours')
    }
    return categoryName
  }

  static multiplier(
    block?: Pick<AttendanceSidepanelCompensationBlockFragment, 'rawMinutesInCents' | 'equivalentMinutesInCents'> &
      DeepPartial<AttendanceSidepanelCompensationBlockFragment>
  ): string {
    return block?.rawMinutesInCents && block?.equivalentMinutesInCents
      ? `${minutesToText(block.rawMinutesInCents / 100)} = ${minutesToText(
          block.equivalentMinutesInCents / 100
        )}`
      : ''
  }

  static isPayroll(block?: {
    compensationPool?: AttendanceSidepanelCompensationBlockFragment['compensationPool']
  }): boolean {
    return block?.compensationPool === 'payroll'
  }

  static isExtraHoursFromBankOfHours(block?: {
    compensationType?: AttendanceSidepanelCompensationBlockFragment['compensationType']
  }): boolean {
    return block?.compensationType === 'extra_hours_from_bank_of_hours'
  }

  static isTimeOff(block?: {
    compensationPool?: AttendanceSidepanelCompensationBlockFragment['compensationPool']
  }): boolean {
    return block?.compensationPool === 'timeoff'
  }

  static isBalance(block?: {
    compensationPool?: AttendanceSidepanelCompensationBlockFragment['compensationPool']
  }): boolean {
    return block?.compensationPool === 'balance'
  }

  static calculateTotalMinutes(
    blocks?: Array<
      Pick<AttendanceSidepanelCompensationBlockFragment, 'minutes'> & DeepPartial<AttendanceSidepanelCompensationBlockFragment>
    >
  ): number {
    return blocks?.reduce((acc, block) => acc + Number(block.minutes), 0) || 0
  }

  static getCompensationDateRange({
    startOn,
    endOn,
    blocks,
  }: {
    startOn: string
    endOn: string
    blocks: Array<
      Pick<AttendanceSidepanelCompensationBlockFragment, 'startOn' | 'endOn'> &
        DeepPartial<AttendanceSidepanelCompensationBlockFragment>
    >
  }): string {
    if (!blocks || blocks.length === 0) return formatBackendDateRange(startOn, endOn)

    const startDates = blocks.map((block) => new Date(block.startOn).getTime())
    const endDates = blocks.map((block) => new Date(block.endOn).getTime())
    const earliestStart = new Date(Math.min(...startDates)).toISOString().split('T')[0] || startOn
    const latestEnd = new Date(Math.max(...endDates)).toISOString().split('T')[0] || endOn
    return formatBackendDateRange(earliestStart, latestEnd)
  }
}

export default CompensationBlockUtils
